import type { Product } from "../../../mocks/products";
import { useCart } from "../../../contexts/CartContext";

interface ProductCardProps {
  product: Product;
  tall?: boolean;
}

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <i
          key={s}
          className={`text-xs ${
            s <= Math.round(rating) ? "ri-star-fill text-amber-400" : "ri-star-line text-zinc-300"
          }`}
        />
      ))}
    </div>
  );
}

export default function ProductCard({ product, tall = false }: ProductCardProps) {
  const { addItem } = useCart();

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    addItem(product);
  };

  return (
    <div className="group cursor-pointer">
      {/* Image */}
      <div
        className="relative overflow-hidden bg-zinc-50 rounded-lg mb-4"
        style={{ aspectRatio: tall ? "4/5.5" : "4/5" }}
      >
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {/* Discount */}
        <div className="absolute top-3 left-3 w-11 h-11 flex items-center justify-center bg-[#1B4332] text-white text-[11px] font-bold rounded-full">
          -{product.discount}%
        </div>
        {/* Add to cart */}
        <button
          onClick={handleAdd}
          className="absolute bottom-4 right-4 w-11 h-11 flex items-center justify-center bg-white text-[#1B4332] rounded-full opacity-0 group-hover:opacity-100 hover:bg-[#1B4332] hover:text-white shadow-lg transition-all duration-300 cursor-pointer"
          aria-label="加入購物車"
        >
          <i className="ri-shopping-bag-line text-sm"></i>
        </button>
      </div>

      {/* Info */}
      <div className="px-1">
        <p className="text-[10px] text-zinc-400 tracking-widest uppercase mb-1">{product.brand}</p>
        <h3 className="text-[#1B4332] font-medium text-sm leading-snug mb-2 line-clamp-2 min-h-[2.5rem]">
          {product.name}
        </h3>
        <div className="flex items-center justify-between mb-2">
          <StarRating rating={product.rating} />
          <span className="text-xs text-zinc-400">({product.reviewCount.toLocaleString()})</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-[#1B4332] text-base font-semibold">
            NT$ {product.price.toLocaleString()}
          </span>
          <span className="text-zinc-400 text-xs line-through">
            NT$ {product.originalPrice.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
}
